import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, Calendar, MessageSquare, RefreshCw, Plus, Search, 
  MapPin, ShieldCheck, CalendarDays, IndianRupee, Tag, LogOut,
  Edit, Trash2, ChevronLeft, ChevronRight, X, Percent
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Helper function to extract readable error strings from FastAPI validation objects
const formatErrorMsg = (err, fallbackMsg = "An error occurred") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  }
  if (typeof detail === "object" && detail !== null) {
    return detail.msg || JSON.stringify(detail);
  }
  return fallbackMsg;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [activeTab, setActiveTab] = useState("users"); // 'users' | 'events' | 'coupons' | 'feedback'
  const [loading, setLoading] = useState(false);

  // Data States
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination State for Users (9 per page)
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 9;

  // New / Edit Event Form State
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    location: "",
    price_per_event: "",
    start_date: "",
    end_date: "",
  });

  // New Coupon Form State
  const [couponForm, setCouponForm] = useState({
    code: "",
    discount_percent: "",
    is_first_time_only: true,
  });

  const autoStatus = useMemo(() => {
    if (!eventForm.start_date || !eventForm.end_date) return "upcoming";
    const today = new Date().toISOString().split("T")[0];
    const start = eventForm.start_date;
    const end = eventForm.end_date;

    if (end < today) {
      return "past";
    } else if (start <= today && today <= end) {
      return "current";
    } else {
      return "upcoming";
    }
  }, [eventForm.start_date, eventForm.end_date]);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, eventsRes, couponsRes, logsRes] = await Promise.all([
        api.get("/admin/users").catch(() => ({ data: [] })),
        api.get("/admin/events").catch(() => ({ data: [] })),
        api.get("/admin/coupons").catch(() => ({ data: [] })),
        api.get("/admin/deletion-logs").catch(() => ({ data: [] })),
      ]);

      if (!isMountedRef.current) return;

      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      setCoupons(Array.isArray(couponsRes.data) ? couponsRes.data : []);
      setDeletionLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
    } catch (err) {
      if (isMountedRef.current) toast.error("Failed to sync Admin data.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.location || !eventForm.start_date || !eventForm.end_date) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (eventForm.end_date < eventForm.start_date) {
      toast.error("End Date cannot be earlier than Start Date.");
      return;
    }

    const payload = {
      ...eventForm,
      price_per_event: Number(eventForm.price_per_event) || 0,
      status: autoStatus,
      is_active: true
    };

    try {
      if (editingEventId) {
        await api.put(`/admin/events/${editingEventId}`, payload);
        toast.success("Event updated successfully!");
      } else {
        await api.post("/admin/events", payload);
        toast.success(`Event created successfully as '${autoStatus.toUpperCase()}'!`);
      }
      handleCancelEdit();
      fetchData();
    } catch (err) {
      toast.error(formatErrorMsg(err, "Failed to save event."));
    }
  };

  const handleStartEdit = (event) => {
    setEditingEventId(event.id);
    setEventForm({
      title: event.title || "",
      description: event.description || "",
      location: event.location || "",
      price_per_event: event.price_per_event ?? event.price_per_day ?? "",
      start_date: event.start_date || "",
      end_date: event.end_date || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEventForm({ title: "", description: "", location: "", price_per_event: "", start_date: "", end_date: "" });
  };

  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) return;
    try {
      await api.delete(`/admin/events/${eventId}`);
      toast.success("Event deleted successfully.");
      fetchData();
    } catch (err) {
      toast.error(formatErrorMsg(err, "Failed to delete event."));
    }
  };

  // Handle Dynamic Coupon Creation
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!couponForm.code || !couponForm.discount_percent) {
      toast.error("Code and discount percentage are required.");
      return;
    }

    try {
      await api.post("/admin/coupons", {
        code: couponForm.code.trim().toUpperCase(),
        discount_percent: Number(couponForm.discount_percent),
        is_first_time_only: couponForm.is_first_time_only,
        is_active: true
      });
      toast.success(`Coupon '${couponForm.code.toUpperCase()}' created successfully!`);
      setCouponForm({ code: "", discount_percent: "", is_first_time_only: true });
      fetchData();
    } catch (err) {
      toast.error(formatErrorMsg(err, "Failed to create coupon."));
    }
  };

  const handleDeleteCoupon = async (couponId, code) => {
    if (!window.confirm(`Are you sure you want to delete coupon '${code}'?`)) return;
    try {
      await api.delete(`/admin/coupons/${couponId}`);
      toast.success("Coupon deleted.");
      fetchData();
    } catch (err) {
      toast.error(formatErrorMsg(err, "Failed to delete coupon."));
    }
  };

  const handleLogout = () => {
    if (logout) logout();
    navigate("/auth/login");
    toast.success("Logged out successfully.");
  };

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.company_name || "").toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  useEffect(() => {
    setUserPage(1);
  }, [searchQuery]);

  const totalUserPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE) || 1;
  const paginatedUsers = useMemo(() => {
    const start = (userPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, userPage]);

  return (
    <div className="min-h-screen bg-[#080808] text-zinc-100 font-sans pb-20 selection:bg-orange-500 selection:text-black">
      
      {/* HEADER SECTION */}
      <header className="bg-black/90 backdrop-blur-xl border-b border-zinc-800/80 sticky top-0 z-50 py-4 px-4 sm:px-8 shadow-2xl">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-500/20 shrink-0">
                <ShieldCheck className="w-5 h-5 text-black" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-full border border-orange-500/20">
                    SuperAdmin Console
                  </span>
                </div>
                <h1 className="text-xl font-black text-white tracking-tight">
                  ExpoLead <span className="text-orange-500">Control Panel</span>
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={fetchData}
                className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-white transition"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-orange-500" : ""}`} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl hover:bg-red-500/20 transition"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 overflow-x-auto pb-1 md:pb-0 hide-scrollbar w-full md:w-auto">
            <div className="flex bg-zinc-900/90 p-1 rounded-2xl border border-zinc-800/80 w-full md:w-auto min-w-max">
              <button
                onClick={() => setActiveTab("users")}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === "users" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-black font-extrabold" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Users className="w-4 h-4" /> Users ({users.length})
              </button>

              <button
                onClick={() => setActiveTab("events")}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === "events" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-black font-extrabold" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Calendar className="w-4 h-4" /> Events ({events.length})
              </button>

              <button
                onClick={() => setActiveTab("coupons")}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === "coupons" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-black font-extrabold" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Tag className="w-4 h-4" /> Coupons ({coupons.length})
              </button>

              <button
                onClick={() => setActiveTab("feedback")}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === "feedback" ? "bg-gradient-to-r from-orange-500 to-amber-500 text-black font-extrabold" : "text-zinc-400 hover:text-white"
                }`}
              >
                <MessageSquare className="w-4 h-4" /> Exit Feedback ({deletionLogs.length})
              </button>
            </div>

            <button
              onClick={fetchData}
              className="hidden md:flex p-2.5 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-400 hover:text-white transition shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-orange-500" : ""}`} />
            </button>

            <button
              onClick={handleLogout}
              className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 font-bold text-xs rounded-2xl transition shrink-0 cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>

        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 pt-8">
        
        {/* TAB 1: USERS DIRECTORY */}
        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-zinc-900/60 border border-zinc-800 p-5 rounded-3xl">
              <div>
                <h2 className="text-lg font-black text-white">Registered Exhibitors Directory</h2>
                <p className="text-xs text-zinc-400">Showing 9 accounts per page. Total Directory Accounts: {filteredUsers.length}</p>
              </div>

              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search name, email, company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 outline-none focus:border-orange-500 transition"
                />
              </div>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-zinc-300">
                  <thead className="bg-black/60 text-zinc-400 font-bold uppercase tracking-wider border-b border-zinc-800">
                    <tr>
                      <th className="py-4 px-6">User Details</th>
                      <th className="py-4 px-6">Company</th>
                      <th className="py-4 px-6 text-center">Subscription</th>
                      <th className="py-4 px-6 text-center">Role</th>
                      <th className="py-4 px-6 text-right">Registration Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-zinc-500">No matching users found.</td>
                      </tr>
                    ) : (
                      paginatedUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-zinc-800/30 transition">
                          <td className="py-4 px-6">
                            <div className="font-bold text-white">{u.name}</div>
                            <div className="text-zinc-500">{u.email}</div>
                          </td>
                          <td className="py-4 px-6 font-medium text-zinc-300">{u.company_name || "—"}</td>
                          <td className="py-4 px-6 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                              u.subscription_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-800 text-zinc-400"
                            }`}>
                              {u.subscription_active ? "Active" : "Free Tier"}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                              u.is_admin ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" : "bg-zinc-800 text-zinc-400"
                            }`}>
                              {u.is_admin ? "Admin" : "Exhibitor"}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right font-mono text-zinc-500">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalUserPages > 1 && (
                <div className="flex items-center justify-between p-4 bg-zinc-950/80 border-t border-zinc-800/80">
                  <span className="text-xs text-zinc-400 font-medium">
                    Showing {((userPage - 1) * USERS_PER_PAGE) + 1} - {Math.min(userPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setUserPage((p) => Math.max(p - 1, 1))}
                      disabled={userPage === 1}
                      className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    <span className="text-xs font-bold text-orange-400 px-3">
                      Page {userPage} of {totalUserPages}
                    </span>

                    <button
                      onClick={() => setUserPage((p) => Math.min(p + 1, totalUserPages))}
                      disabled={userPage === totalUserPages}
                      className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: EVENTS */}
        {activeTab === "events" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 bg-zinc-900/60 border border-zinc-800 p-6 rounded-3xl space-y-5 shadow-2xl">
              <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-orange-500/10 text-orange-500 border border-orange-500/20">
                    {editingEventId ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  </div>
                  <div>
                    <h2 className="text-base font-black text-white">
                      {editingEventId ? "Edit Global Event" : "Add Global Trade Event"}
                    </h2>
                    <p className="text-xs text-zinc-400">
                      {editingEventId ? "Modify event parameters below" : "Publish exhibitions for subscriber sync."}
                    </p>
                  </div>
                </div>

                {editingEventId && (
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 rounded-xl bg-zinc-800 text-zinc-400 hover:text-white transition cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <form onSubmit={handleSaveEvent} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Event Title</label>
                  <input
                    type="text"
                    placeholder="e.g. DEFEXPO India 2026"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Provide event details, stall info, guidelines..."
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Location</label>
                    <input
                      type="text"
                      placeholder="Delhi, India"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Price / Event (₹)</label>
                    <input
                      type="number"
                      placeholder="2500"
                      value={eventForm.price_per_event}
                      onChange={(e) => setEventForm({ ...eventForm, price_per_event: e.target.value })}
                      className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5 flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5 text-orange-500" /> Start Date
                    </label>
                    <input
                      type="date"
                      style={{ colorScheme: "dark" }}
                      value={eventForm.start_date}
                      onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                      className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5 flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5 text-orange-500" /> End Date
                    </label>
                    <input
                      type="date"
                      style={{ colorScheme: "dark" }}
                      value={eventForm.end_date}
                      onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                      className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition cursor-pointer"
                    />
                  </div>
                </div>

                <div className="p-3 bg-zinc-950/80 border border-zinc-800 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-zinc-500" />
                    <span className="text-xs text-zinc-400 font-medium">Auto Status:</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                    autoStatus === "current" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                    autoStatus === "past" ? "bg-zinc-800 text-zinc-400 border border-zinc-700" :
                    "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                  }`}>
                    {autoStatus}
                  </span>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    className="flex-1 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-black font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg shadow-orange-500/20 cursor-pointer"
                  >
                    {editingEventId ? "Update Event" : "Publish Event"}
                  </button>

                  {editingEventId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs rounded-xl transition cursor-pointer"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-xs font-black text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                Active Global Events ({events.length})
              </h2>

              {events.length === 0 ? (
                <div className="p-12 bg-zinc-900/40 border border-zinc-800 rounded-3xl text-center text-zinc-500 text-xs">
                  No events registered in database. Use the form to publish one.
                </div>
              ) : (
                <div className="space-y-3">
                  {events.map((ev) => (
                    <div key={ev.id} className="p-5 bg-zinc-900/60 border border-zinc-800 rounded-3xl hover:border-zinc-700 transition space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-bold text-white text-base">{ev.title}</h3>
                          <p className="text-xs text-zinc-400 flex items-center gap-1 mt-1">
                            <MapPin className="w-3.5 h-3.5 text-orange-500" /> {ev.location}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                            ev.status === "current" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                            ev.status === "past" ? "bg-zinc-800 text-zinc-400 border border-zinc-700" :
                            "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                          }`}>
                            {ev.status}
                          </span>

                          <button
                            onClick={() => handleStartEdit(ev)}
                            className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 transition cursor-pointer"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>

                          <button
                            onClick={() => handleDeleteEvent(ev.id, ev.title)}
                            className="p-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-zinc-400 pt-3 border-t border-zinc-800/80 font-mono">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                          {ev.start_date} to {ev.end_date}
                        </div>
                        <div className="font-bold text-orange-400 flex items-center gap-0.5">
                          <IndianRupee className="w-3.5 h-3.5" /> {ev.price_per_event ?? ev.price_per_day} / event
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: DYNAMIC COUPONS */}
        {activeTab === "coupons" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 bg-zinc-900/60 border border-zinc-800 p-6 rounded-3xl space-y-5 shadow-2xl">
              <div className="flex items-center gap-3 pb-3 border-b border-zinc-800">
                <div className="p-2.5 rounded-xl bg-orange-500/10 text-orange-500 border border-orange-500/20">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-black text-white">Create Custom Coupon</h2>
                  <p className="text-xs text-zinc-400">Add dynamic discount codes to MySQL database.</p>
                </div>
              </div>

              <form onSubmit={handleCreateCoupon} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Coupon Code</label>
                  <input
                    type="text"
                    placeholder="e.g. EXPO50"
                    value={couponForm.code}
                    onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs font-mono uppercase text-white outline-none focus:border-orange-500 transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1.5">Discount Percentage (%)</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="e.g. 50"
                    value={couponForm.discount_percent}
                    onChange={(e) => setCouponForm({ ...couponForm, discount_percent: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white outline-none focus:border-orange-500 transition"
                  />
                </div>

                <div className="flex items-center gap-3 p-3 bg-zinc-950/80 border border-zinc-800 rounded-2xl">
                  <input
                    type="checkbox"
                    id="firstTimeCheck"
                    checked={couponForm.is_first_time_only}
                    onChange={(e) => setCouponForm({ ...couponForm, is_first_time_only: e.target.checked })}
                    className="w-4 h-4 accent-orange-500 rounded cursor-pointer"
                  />
                  <label htmlFor="firstTimeCheck" className="text-xs text-zinc-300 font-bold cursor-pointer">
                    First-Time Customers Only
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-black font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg shadow-orange-500/20 cursor-pointer"
                >
                  Save Coupon
                </button>
              </form>
            </div>

            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-xs font-black text-zinc-400 uppercase tracking-widest">
                Database Coupons ({coupons.length})
              </h2>

              {coupons.length === 0 ? (
                <div className="p-12 bg-zinc-900/40 border border-zinc-800 rounded-3xl text-center text-zinc-500 text-xs">
                  No coupons found in database. Create one using the form.
                </div>
              ) : (
                <div className="space-y-3">
                  {coupons.map((c) => (
                    <div key={c.id} className="p-5 bg-zinc-900/60 border border-zinc-800 rounded-3xl flex items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-black text-white text-base">{c.code}</span>
                          <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 text-[10px] font-black px-2.5 py-0.5 rounded-full">
                            {c.discount_percent}% OFF
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-500">
                          {c.is_first_time_only ? "First-time buyers only" : "All buyers allowed"}
                        </p>
                      </div>

                      <button
                        onClick={() => handleDeleteCoupon(c.id, c.code)}
                        className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: EXIT FEEDBACK */}
        {activeTab === "feedback" && (
          <div className="space-y-4">
            <div className="bg-zinc-900/60 border border-zinc-800 p-5 rounded-3xl">
              <h2 className="text-lg font-black text-white">Exit Feedback Logs</h2>
              <p className="text-xs text-zinc-400">Insights from account deactivations.</p>
            </div>

            <div className="space-y-3">
              {deletionLogs.length === 0 ? (
                <div className="p-12 bg-zinc-900/40 border border-zinc-800 rounded-3xl text-center text-zinc-500 text-xs">
                  No deletion logs recorded yet.
                </div>
              ) : (
                deletionLogs.map((log) => (
                  <div key={log.id} className="p-5 bg-zinc-900/60 border border-zinc-800 rounded-3xl space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-white">{log.user_email}</span>
                      <span className="text-zinc-500 font-mono">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-zinc-300 italic bg-black/40 p-3 rounded-xl border border-zinc-800">
                      "{log.reason}"
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}